// Push notifications, one entry point for both platforms:
//  * Android app: @capacitor/push-notifications (FCM), registered after login
//  * web: Firebase JS SDK (see webpush.ts), only after an explicit user action
// Foreground messages → badges + in-app banner (live.ts); taps → data.url.

import { Capacitor } from '@capacitor/core';
import { api } from '../api/client';
import { onBeforeLogout } from '../auth/oidc';
import { navigate } from './router';
import { handlePush, safeRoute } from './live';

const TOKEN_KEY = 'booth.pushToken';
const OPTOUT_KEY = 'booth.pushOff';
let started = false;

const ls = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string | null) => { try { if (v) localStorage.setItem(k, v); else localStorage.removeItem(k); } catch { /* */ } },
};

const native = () => Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('PushNotifications');

export type PushState = 'on' | 'off' | 'blocked' | 'unsupported';

/** Saves the token server-side (and forgets an older one). */
async function saveToken(token: string, platform: 'android' | 'web') {
  const old = ls.get(TOKEN_KEY);
  await api.registerDevice(token, platform);
  ls.set(TOKEN_KEY, token);
  if (old && old !== token) void api.unregisterDevice(old).catch(() => {});
}

// ------------------------------------------------------------------ Android

async function nativeStart(ask: boolean): Promise<PushState> {
  const { PushNotifications } = await import('@capacitor/push-notifications');
  if (!started) {
    started = true;
    await PushNotifications.addListener('registration', ({ value }) => {
      saveToken(value, 'android').catch((e) => console.warn('push: device registration failed', e));
    });
    await PushNotifications.addListener('registrationError', (e) => console.warn('push: registration error', e));
    // app in the foreground: the system shows nothing, we update badges + show the banner
    await PushNotifications.addListener('pushNotificationReceived', (n) => {
      handlePush({ title: n.title, body: n.body, data: (n.data ?? {}) as Record<string, string> });
    });
    // tapped in the system tray
    await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      const to = safeRoute(notification.data?.url);
      if (to) navigate(to);
    });
    await PushNotifications.createChannel({
      id: 'booth_default', name: 'Booth Together', description: 'Bình luận, lời mời, ảnh mới', importance: 4, visibility: 1,
    }).catch(() => {});
  }
  let perm = await PushNotifications.checkPermissions();
  if (ask && (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale')) perm = await PushNotifications.requestPermissions();
  if (perm.receive === 'denied') return 'blocked';
  if (perm.receive !== 'granted') return 'off';
  await PushNotifications.register();
  return 'on';
}

// ------------------------------------------------------------------ public API

/** After login. Android: registers (asks once, like before). Web: only re-attaches an existing grant. */
export async function initPush() {
  try {
    if (ls.get(OPTOUT_KEY)) return;
    if (native()) await nativeStart(true);
    else {
      const web = await import('./webpush');
      if (web.webPushConfigured() && web.permission() === 'granted') await web.startWebPush(saveToken);
    }
  } catch (e) {
    console.warn('push unavailable', e);
  }
}

export async function pushState(): Promise<PushState> {
  try {
    if (native()) {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const p = await PushNotifications.checkPermissions();
      if (p.receive === 'denied') return 'blocked';
      return p.receive === 'granted' && !ls.get(OPTOUT_KEY) ? 'on' : 'off';
    }
    const web = await import('./webpush');
    if (!web.webPushConfigured() || !(await web.webPushSupported())) return 'unsupported';
    const p = web.permission();
    if (p === 'denied') return 'blocked';
    return p === 'granted' && !!ls.get(TOKEN_KEY) && !ls.get(OPTOUT_KEY) ? 'on' : 'off';
  } catch {
    return 'unsupported';
  }
}

/** From a user action (button/toggle): asks permission if needed and registers. */
export async function enablePush(): Promise<PushState> {
  ls.set(OPTOUT_KEY, null);
  if (native()) return nativeStart(true);
  const web = await import('./webpush');
  if (!web.webPushConfigured()) return 'unsupported';
  return web.startWebPush(saveToken, true);
}

/** Stops notifications for this device (keeps the browser/OS permission). */
export async function disablePush() {
  ls.set(OPTOUT_KEY, '1');
  await dropToken();
}

async function dropToken() {
  const t = ls.get(TOKEN_KEY);
  ls.set(TOKEN_KEY, null);
  if (t) await api.unregisterDevice(t).catch(() => {});
  if (native()) {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.unregister().catch(() => {});
  } else {
    const web = await import('./webpush').catch(() => null);
    await web?.stopWebPush().catch(() => {});
  }
}

// logout: unregister this device while the access token is still valid
onBeforeLogout(async () => {
  await dropToken();
  if (native()) {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.removeAllListeners().catch(() => {});
    started = false;
  }
});
